import SessionContents from '../../enums/SessionContents.js';
import ResultType from '../../enums/ResultType.js';
import PitchData from '../../enums/PitchData.js';
import MlbSensorDataConverter from './MlbSensorDataConverter.js';
import MlbTaggingDataConverter from './MlbTaggingDataConverter.js';
import MlbScoringDataConverter from './MlbScoringDataConverter.js';

class MlbPlaysConverter {
    constructor(sessionContents, sensorDataConverter, taggingDataConverter, scoringDataConverter) {
        // TODO - enums
        this.resultTemplate = {
            // based on @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/result-v2.json
            id: null,
            pitchResultID: null,
            hitResultID: null,
            sessionID: null,
            resultType: null,
            createDate: null,
            isInvalid: false,
            pitcherTagID: null,
            batterTagID: null,
            catcherTagID: null,
            data: {
                common: {
                    // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-common-v1.json
                    [PitchData.Guid.key]: null,
                    ['CREATE_DATE_UTC']: null, // TODO - enum?
                    ['TRACKING_MODE']: 'OBPH',
                    ['BAT_VALID']: false,
                    ['PITCH_VALID']: null,
                    ['HIT_VALID']: null,
                },
            },
            isDeleted: false,
        };

        this.sensorDataConverter = null;
        this.taggingDataConverter = null;
        this.scoringDataConverter = null;

        const withSensor = [
            SessionContents.R, SessionContents.RT, SessionContents.RST, SessionContents.RS,
        ];
        const wihTagging = [
            SessionContents.T, SessionContents.RT, SessionContents.RST, SessionContents.TS,
        ];
        const withScoring = [
            SessionContents.S, SessionContents.TS, SessionContents.RST, SessionContents.RS,
        ];

        if (withSensor.includes(sessionContents)) {
            this.resultTemplate.data.sensor = {
                pitch: null,
                hit: null,
                poptime: null,
            };

            if (typeof(sensorDataConverter) !== 'undefined' && !(sensorDataConverter instanceof MlbSensorDataConverter)) {
                throw new TypeError('sensorDataConverter must be undefined (to use default implementation) or MlbSensorDataConverter\'s class instance');
            }

            this.sensorDataConverter = sensorDataConverter || new MlbSensorDataConverter();
        }

        if (wihTagging.includes(sessionContents)) {
            this.resultTemplate.data.tagging = {
                pitch: null,
                hit: null,
            };

            if (typeof(taggingDataConverter) !== 'undefined' && !(taggingDataConverter instanceof MlbTaggingDataConverter)) {
                throw new TypeError('taggingDataConverter must be undefined (to use default implementation) or MlbTaggingDataConverter\'s class instance');
            }

            this.taggingDataConverter = taggingDataConverter || new MlbTaggingDataConverter();
        }

        if (withScoring.includes(sessionContents)) {
            this.resultTemplate.data.scoring = {};

            if (typeof(scoringDataConverter) !== 'undefined' && !(scoringDataConverter instanceof MlbScoringDataConverter)) {
                throw new TypeError('scoringDataConverter must be undefined (to use default implementation) or MlbScoringDataConverter\'s class instance');
            }

            this.scoringDataConverter = scoringDataConverter || new MlbScoringDataConverter();
            // this.scoringDataConverter.reset();
        }
    }

    reset() {
        this.scoringDataConverter.reset();
    }

    /**
     * Converts single play from MLB feed (liveData.plays.allPlays[]) into multiple results
     * @param {Object} play 
     * @returns {Array} [] of results
     */
    convert(play, sessionId) {
        // TODO - entity
        if (!play || typeof(play) !== 'object') {
            throw new TypeError('game must be an object');
        }

        const results = [];
        for (let pe = 0; pe < play.playEvents.length; pe++) {
            const playEvent = play.playEvents[pe];
            if (!playEvent.isPitch) {
                // announcments, pitch mound visits etc
                continue;
            }

            const result = this.createResultTemplate();
            result.batterTagID = play.matchup.batter.id;
            result.pitcherTagID = play.matchup.pitcher.id;
            result.sessionID = sessionId || 0;
            result.createDate = playEvent.startTime;
            result.resultType = this.determineResultType(playEvent);
            if (!result.resultType) {
                throw new Error(`Unable to determine result type for playEvent ${playEvent.id}`);
            }

            result.id = this.determineFakeResultId(play, playEvent, sessionId);
            this.readCommonData(playEvent, result);
            switch (result.resultType) {
                case ResultType.PitchHit.key:
                    result.pitchResultID = result.id;
                    result.hitResultID = result.id + 1;
                    break;

                case ResultType.Pitch.key:
                    result.pitchResultID = result.id;
                    break;

                case ResultType.Hit.key:
                    result.hitResultID = result.id + 1;
                    break;

                default:
                    throw new Error(`Unknown type of result determined  - ${result.resultType} - for playEvent ${playEvent.id}`);
            }

            // let's get the measured data!
            if (this.sensorDataConverter) {
                result.data.sensor = Object.assign(
                    result.data.sensor, this.sensorDataConverter.convert(playEvent)
                )
            }

            // and scoring!
            if (this.scoringDataConverter) {
                result.data.scoring = Object.assign(
                    result.data.scoring, this.scoringDataConverter.convert(play, playEvent)
                )
            }

            // TODO - handle catcher

            // so do tagging
            if (this.taggingDataConverter) {
                result.data.tagging = Object.assign(
                    result.data.tagging, this.taggingDataConverter.convert(play, playEvent)
                )
            }

            results.push(result);
        }

        return results;
    }

    readCommonData(playEvent, result) {
        result.data.common[PitchData.Guid.key] = `{${playEvent.playId}}`;
        result.data.common['CREATE_DATE_UTC'] = (new Date(playEvent.startTime).toISOString());
        result.data.common['PITCH_VALID'] = typeof(playEvent.pitchData) !== 'undefined'
        result.data.common['HIT_VALID'] = typeof(playEvent.hitData) !== 'undefined';
    }

    createResultTemplate() {
        const result = Object.assign({}, this.resultTemplate);
        // we need to loose all refeerences, otherwise it can create a mess
        result.data = Object.assign(
            {}, this.resultTemplate.data
        );
        result.data.common = Object.assign(
            {}, this.resultTemplate.data.common
        );
        if (result.data.sensor) {
            result.data.sensor = Object.assign(
                {}, this.resultTemplate.data.sensor
            );
        }
        if (result.data.tagging) {
            result.data.tagging = Object.assign(
                {}, this.resultTemplate.data.tagging
            );
        }
        if (result.data.scoring) {
            result.data.scoring = Object.assign(
                {}, this.resultTemplate.data.scoring
            );
        }

        return result;
    }

    determineResultType(playEvent) {
        const hasPitch = typeof(playEvent.pitchData) !== 'undefined';
        const hasHit = typeof(playEvent.hitData) !== 'undefined';

        if (hasPitch && hasHit) {
            return ResultType.PitchHit.key;
        }

        if (hasPitch) {
            return ResultType.Pitch.key;
        }

        if (hasHit) {
            return ResultType.Hit.key;
        }

        return null;
    }

    determineFakeResultId(play, playEvent, sessionId) {
        let resultId = sessionId * 10000;
        resultId += (play.about.inning - 1) * 200 + (play.about.isTopInning ? 0: 100);
        resultId += playEvent.pitchNumber * 2;
        return resultId;
    }
}

export default MlbPlaysConverter;